import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Phone, FileText, Calendar, AlertTriangle, Building2, CheckCircle, MessageSquare, Columns3, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MessengerIcon } from "@/components/icons/MessengerIcon";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LeadTagBadge } from "./LeadTagBadge";
import { LeadAssignedMemberLabel } from "./LeadAssignedMemberLabel";
import type { Lead, LeadTag } from "@/types/lead";
import { isHumanHandoffVisualStage, type LeadStageConfig } from "@/lib/lead-stage-agent-handoff";

const META_CHIP =
  "inline-flex items-center gap-1 h-5 max-w-full rounded-full px-2 text-[10px] font-medium";

const InstagramIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export interface LeadCardProps {
  lead: Lead;
  onClick: () => void;
  onChatClick?: () => void;
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  // Pre-fetched data passed from parent (eliminates N+1 queries)
  tags?: LeadTag[];
  primaryGroup?: { name: string; role?: string } | null;
  isClosed?: boolean;
  assignedMemberName?: string | null;
  unreadCount?: number;
  sortableProps?: React.HTMLAttributes<HTMLDivElement>;
  handoffStagesMap?: Map<string, LeadStageConfig>;
  kanbanMobile?: boolean;
  moveStages?: { slug: string; name: string; color: string }[];
  onMoveToStage?: (lead: Lead, stageSlug: string) => void;
}

export function LeadCard({
  lead,
  onClick,
  onChatClick,
  selectionMode,
  isSelected,
  onToggleSelect,
  tags,
  primaryGroup,
  isClosed,
  assignedMemberName,
  unreadCount,
  sortableProps,
  handoffStagesMap,
  kanbanMobile,
  moveStages = [],
  onMoveToStage,
}: LeadCardProps) {
  // Use pre-fetched data from props (no internal queries)
  const displayTags = tags || lead.tags || [];
  const isUrgentHandoff = isHumanHandoffVisualStage(lead.stage, handoffStagesMap);

  return (
    <div
      {...sortableProps}
      className={cn(
        "group overflow-hidden border bg-card hover:bg-muted/50 transition-colors",
        kanbanMobile ? "p-4 rounded-2xl shadow-sm" : "p-3 rounded-lg",
        sortableProps ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
        isUrgentHandoff && "ring-2 ring-orange-500 bg-orange-50 dark:bg-orange-950/20",
        selectionMode && isSelected && "ring-2 ring-primary bg-primary/5"
      )}
      onClick={selectionMode ? () => onToggleSelect?.(lead.id) : onClick}
    >
      {!kanbanMobile && assignedMemberName ? (
        <LeadAssignedMemberLabel memberName={assignedMemberName} />
      ) : null}
      <div className="flex items-start gap-2">
        {selectionMode ? (
          <div className="mt-0.5">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect?.(lead.id)}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        ) : null}
        
        <div className="min-w-0 flex-1 space-y-2 overflow-hidden">
          <div className="flex min-w-0 items-center gap-2">
            {lead.channel === 'instagram' && (
              <InstagramIcon className="h-4 w-4 shrink-0 text-pink-500" />
            )}
            {(lead.channel?.includes('whatsapp')) && (
              <WhatsAppIcon className="h-4 w-4 shrink-0 text-green-500" />
            )}
            {lead.channel === 'messenger' && (
              <MessengerIcon className="h-4 w-4 shrink-0 text-blue-500" />
            )}
            <p className="min-w-0 flex-1 truncate font-medium leading-tight">{lead.name}</p>
            {unreadCount && unreadCount > 0 ? (
              <div className="bg-green-500 text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 shrink-0">
                {unreadCount}
              </div>
            ) : null}
            {kanbanMobile && onMoveToStage && moveStages.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    aria-label="Mais ações"
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuLabel className="flex items-center gap-2 font-normal text-muted-foreground">
                    <Columns3 className="h-3.5 w-3.5" />
                    Mover para
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {moveStages.map((stage) => (
                    <DropdownMenuItem
                      key={stage.slug}
                      onClick={() => onMoveToStage(lead, stage.slug)}
                    >
                      <span className={cn("h-2 w-2 rounded-full mr-2 shrink-0", stage.color)} />
                      <span className="truncate">{stage.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {kanbanMobile ? (
            <div className="flex flex-wrap items-center gap-1">
              {isUrgentHandoff && (
                <Badge variant="destructive" className="h-5 rounded-full border-0 px-2 text-[10px]">
                  <AlertTriangle className="mr-1 h-3 w-3 shrink-0" />
                  Urgente
                </Badge>
              )}
              {isClosed && (
                <span className={cn(META_CHIP, "bg-muted text-muted-foreground")}>
                  <CheckCircle className="h-3 w-3 shrink-0" />
                  Encerrado
                </span>
              )}
              {lead.current_appointment_id && lead.current_appointment_date && (
                <span className={cn(META_CHIP, "bg-teal-500/10 text-teal-800 dark:text-teal-300")}>
                  <Calendar className="h-3 w-3 shrink-0" />
                  {new Date(lead.current_appointment_date).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              )}
              {primaryGroup && (
                <span className={cn(META_CHIP, "bg-amber-500/10 text-amber-900 dark:text-amber-300")}>
                  <Building2 className="h-3 w-3 shrink-0" />
                  <span className="truncate">
                    {primaryGroup.name}
                    {primaryGroup.role ? ` · ${primaryGroup.role}` : ""}
                  </span>
                </span>
              )}
              {assignedMemberName && (
                <span className={cn(META_CHIP, "bg-primary/10 text-primary")}>
                  {assignedMemberName}
                </span>
              )}
              {lead.phone && lead.channel !== "instagram" && (
                <span className={cn(META_CHIP, "bg-emerald-500/10 text-emerald-800 dark:text-emerald-300")}>
                  <Phone className="h-3 w-3 shrink-0" />
                  <span className="truncate">{lead.phone}</span>
                </span>
              )}
              {lead.email && (
                <span className={cn(META_CHIP, "bg-slate-500/10 text-slate-700 dark:text-slate-300")}>
                  <Mail className="h-3 w-3 shrink-0" />
                  <span className="truncate">{lead.email}</span>
                </span>
              )}
            </div>
          ) : (
            <>
              {(isUrgentHandoff || isClosed || (lead.current_appointment_id && lead.current_appointment_date)) && (
                <div className="flex flex-wrap gap-1">
                  {isUrgentHandoff && (
                    <Badge variant="destructive" className="max-w-full text-xs">
                      <AlertTriangle className="mr-1 h-3 w-3 shrink-0" />
                      Urgente
                    </Badge>
                  )}
                  {isClosed && (
                    <Badge variant="secondary" className="max-w-full bg-muted text-xs text-muted-foreground">
                      <CheckCircle className="mr-1 h-3 w-3 shrink-0" />
                      Encerrado
                    </Badge>
                  )}
                  {lead.current_appointment_id && lead.current_appointment_date && (
                    <Badge variant="secondary" className="max-w-full text-xs">
                      <Calendar className="mr-1 h-3 w-3 shrink-0" />
                      {new Date(lead.current_appointment_date).toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Badge>
                  )}
                </div>
              )}
              {primaryGroup && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Building2 className="w-3 h-3 text-primary flex-shrink-0" />
                  <span className="truncate font-medium text-foreground">{primaryGroup.name}</span>
                  {primaryGroup.role && <span className="truncate">- {primaryGroup.role}</span>}
                </div>
              )}
            </>
          )}

          {displayTags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {displayTags.slice(0, 3).map((tag) => (
                <LeadTagBadge key={tag.id} tag={tag} size="sm" />
              ))}
              {displayTags.length > 3 && (
                <span className="text-xs text-muted-foreground px-1">
                  +{displayTags.length - 3}
                </span>
              )}
            </div>
          )}

          {!kanbanMobile && lead.email && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Mail className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{lead.email}</span>
            </div>
          )}

          {!kanbanMobile && lead.phone && lead.channel !== "instagram" && (
            <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
              <Phone className="h-3 w-3 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{lead.phone}</span>
              {!selectionMode && onChatClick ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="h-7 w-7 shrink-0 shadow-sm opacity-80 group-hover:opacity-100"
                  aria-label="Abrir conversa"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChatClick();
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                </Button>
              ) : null}
            </div>
          )}

          {((kanbanMobile && !selectionMode && onChatClick) ||
            ((!lead.phone || lead.channel === "instagram") && !selectionMode && onChatClick && !kanbanMobile)) ? (
            <div className={cn("flex", kanbanMobile ? "justify-start" : "justify-end")}>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="h-7 w-7 shrink-0 shadow-sm opacity-80 group-hover:opacity-100"
                aria-label="Abrir conversa"
                onClick={(e) => {
                  e.stopPropagation();
                  onChatClick();
                }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <MessageSquare className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : null}
          
          {lead.notes && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <FileText className="w-3 h-3 flex-shrink-0" />
              <span className="line-clamp-1">{lead.notes}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
